/**
 * DROPIT product page (sections/dropit-product.liquid and the dp-* sections below it).
 * - <dropit-gallery>: swipe/arrows/thumbs, white-background photos become cards, gentle pointer parallax,
 *   follows the selected variant's image.
 * - Cashback, prices and buy proxies follow Horizon's variant (shopify:product:select) and quantity changes.
 * - Mobile buy bar, in-page nav (scroll spy, smooth jumps), "Ler mais".
 * - <dropit-roast-chart>, <dropit-brew> (tabs + timer), <dropit-video> (plays muted only on screen),
 *   recommendations fetched when near the screen.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const root = document.querySelector('[data-dp]');

function onNear(element, callback, margin = '300px 0px') {
  if (!('IntersectionObserver' in window)) return callback();
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      observer.disconnect();
      callback();
    },
    { rootMargin: margin }
  );
  observer.observe(element);
}

function moneyFormatter() {
  const currency = root?.dataset.currency || 'BRL';
  try {
    return new Intl.NumberFormat(root?.dataset.locale || 'pt-BR', { style: 'currency', currency });
  } catch {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency });
  }
}

const clock = (seconds) => {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/* ---------- Gallery ---------- */
class DropitGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.counter = this.querySelector('[data-count]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.index = -1;

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
    this.prevButton?.addEventListener('click', () => this.go(this.index - 1));
    this.nextButton?.addEventListener('click', () => this.go(this.index + 1));
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      },
      { passive: true }
    );
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      else if (event.key === 'ArrowLeft') this.go(this.index - 1);
      else return;
      event.preventDefault();
    });

    for (const slide of this.slides) {
      const img = slide.querySelector('img');
      if (!img) continue;
      const check = () => this.checkOpaque(slide, img);
      if (img.complete && img.naturalWidth) check();
      else img.addEventListener('load', check, { once: true });
    }

    if (!reducedMotion && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('pointermove', (event) => {
        const box = this.getBoundingClientRect();
        this.style.setProperty('--mx', (((event.clientX - box.left) / box.width - 0.5) * 2).toFixed(3));
        this.style.setProperty('--my', (((event.clientY - box.top) / box.height - 0.5) * 2).toFixed(3));
      });
      this.addEventListener('pointerleave', () => {
        this.style.setProperty('--mx', '0');
        this.style.setProperty('--my', '0');
      });
    }

    this.sizeStage();
    addEventListener('resize', () => this.sizeStage(), { passive: true });
    this.sync();
  }

  /** Fill exactly the screen below the header and top bar on first view (desktop) */
  sizeStage() {
    if (!matchMedia('(min-width: 990px)').matches) return this.style.removeProperty('--dp-stage-h');
    const scroller = document.querySelector('.page-wrapper');
    const scrolled = scroller && scroller.scrollHeight > scroller.clientHeight ? scroller.scrollTop : scrollY;
    const top = this.getBoundingClientRect().top + scrolled;
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const visible = innerHeight - Math.max(top, header);
    this.style.setProperty('--dp-stage-h', `${Math.max(520, Math.round(visible))}px`);
  }

  sync() {
    const width = this.track.clientWidth || 1;
    const index = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
    if (index === this.index) return;
    this.index = index;
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
    if (this.prevButton) this.prevButton.disabled = index <= 0;
    if (this.nextButton) this.nextButton.disabled = index >= this.slides.length - 1;
  }

  go(index) {
    const target = Math.min(this.slides.length - 1, Math.max(0, index));
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  goToMedia(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.go(index);
  }

  /** Photos with a solid (white) background get the card treatment instead of floating on the azul */
  checkOpaque(slide, img) {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 16;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(img, 0, 0, 16, 16);
      const data = context.getImageData(0, 0, 16, 16).data;
      const corners = [0, 15, 240, 255].map((pixel) => data[pixel * 4 + 3]);
      slide.classList.toggle('is-opaque', corners.every((alpha) => alpha > 250));
    } catch {
      /* image without CORS: leave it floating */
    }
  }
}

if (!customElements.get('dropit-gallery')) customElements.define('dropit-gallery', DropitGallery);

/* ---------- Price, cashback, buy proxies, mobile bar ---------- */
function initBuy() {
  if (!root) return;
  const money = moneyFormatter();
  const percent = Number(root.dataset.cashback) || 0;
  const state = {
    price: Number(root.dataset.price) || 0,
    available: root.dataset.available === 'true',
    quantity: 1,
  };

  const render = () => {
    const cashback = Math.floor((state.price * state.quantity * percent) / 100);
    root.querySelectorAll('[data-dp-cashback]').forEach((el) => (el.textContent = money.format(cashback / 100)));
    root.querySelectorAll('[data-dp-price]').forEach((el) => (el.textContent = money.format(state.price / 100)));
    root.querySelectorAll('[data-dp-buy]').forEach((button) => (button.disabled = !state.available));
  };

  const readQuantity = () => {
    const input = root.querySelector('.dp-info input[name="quantity"]');
    state.quantity = Math.max(1, parseInt(input?.value, 10) || 1);
    render();
  };

  root.addEventListener('input', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') readQuantity();
  });
  root.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') readQuantity();
  });
  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.quantity-minus, .quantity-plus')) setTimeout(readQuantity, 0);

    const proxy = target?.closest('[data-dp-buy]');
    if (!proxy) return;
    const button = root.querySelector('.dp-info .add-to-cart-button');
    if (button instanceof HTMLButtonElement && !button.disabled) button.click();
    else root.querySelector('.dp-info variant-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
  document.addEventListener('quantity-selector:update', readQuantity);

  const section = root.closest('.shopify-section') || document;
  section.addEventListener('shopify:product:select', (event) => {
    event.promise
      ?.then(({ detail }) => {
        const variant = detail?.resource;
        state.available = Boolean(variant?.available);
        if (variant) {
          state.price = variant.price;
          const label = root.querySelector('[data-dp-variant]');
          if (label && variant.title && variant.title !== 'Default Title') label.textContent = variant.title;
          // Kit: the item list of the chosen kit variant (100g / 250g…)
          root.querySelectorAll('[data-kit-variant]').forEach((list) => {
            list.hidden = list.getAttribute('data-kit-variant') !== String(variant.id);
          });
          // Variant with its own photo (18 g → tube) shows it; the others go back to the first photo
          const gallery = root.querySelector('dropit-gallery');
          if (variant.featured_media?.id) gallery?.goToMedia(variant.featured_media.id);
          else gallery?.go(0);
        }
        updateStock(root, variant);
        readQuantity();
      })
      .catch(() => {});
  });

  const bar = root.querySelector('[data-dp-bar]');
  const buttons = root.querySelector('[data-dp-buttons]');
  if (bar && buttons && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      if (entry) bar.toggleAttribute('data-show', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(buttons);
  }

  render();
}

/* ---------- In-page nav, jumps, "Ler mais" ---------- */
function initNav() {
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-dp-nav], a[data-dp-jump]') : null;
    const id = link?.getAttribute('href')?.slice(1);
    const target = id ? document.getElementById(id) : null;
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  });

  const nav = document.querySelector('.dp-nav');
  if (!nav) return;
  for (const link of nav.querySelectorAll('a[data-dp-nav]')) {
    if (!document.getElementById(link.getAttribute('href').slice(1))) link.closest('li')?.remove();
  }
  const links = [...nav.querySelectorAll('a[data-dp-nav]')];
  if (!links.length) return nav.remove();
  if (!('IntersectionObserver' in window)) return;

  const list = nav.querySelector('.dropit-chips__list');
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        for (const link of links) {
          const current = link.getAttribute('href') === `#${entry.target.id}`;
          link.setAttribute('aria-current', String(current));
          if (current && list) {
            list.scrollTo({ left: link.offsetLeft - 16, behavior: reducedMotion ? 'auto' : 'smooth' });
          }
        }
      }
    },
    { rootMargin: '-40% 0px -55% 0px' }
  );
  links.forEach((link) => {
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (target) observer.observe(target);
  });
}

function initClamp() {
  for (const text of document.querySelectorAll('[data-dp-clamp]')) {
    const toggle = text.parentElement?.querySelector('[data-dp-clamp-toggle]');
    onNear(
      text,
      () => {
        text.setAttribute('data-clamped', '');
        if (text.scrollHeight <= text.clientHeight + 24) {
          text.removeAttribute('data-clamped');
          return;
        }
        if (!toggle) return;
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          const open = text.hasAttribute('data-clamped');
          text.toggleAttribute('data-clamped', !open);
          toggle.textContent = open ? 'Ler menos' : 'Ler mais';
        });
      },
      '0px'
    );
  }
}

/* ---------- Roast chart ---------- */
/**
 * How a drum roast reads (Cropster/Artisan conventions, Rao's "declining RoR"):
 * - BT (bean probe) falls after charge to the turning point (~1:00–1:30, ~90–100 °C), then rises with a rate of rise
 *   that peaks right after TP and declines steadily to the end (no crash or flick at first crack).
 * - Drying ends at yellowing (~150 °C, ~40–45 % of the roast); Maillard runs to first crack (~196 °C);
 *   development is first crack → drop (~15–25 % of the roast, shorter for lighter roasts).
 * - ET (air in the drum) dips after charge, then climbs and always stays above BT, flattening after first crack.
 * Illustrative presets per DROPIT roast level (clara → média escura), solved so the events land on those values.
 */
const ROAST_PRESETS = {
  1: { drop: 590, peak: 22, end: 4.5, shape: 1.2 },
  2: { drop: 610, peak: 19.5, end: 4, shape: 0.8 },
  3: { drop: 630, peak: 19.5, end: 6.5, shape: 1.2 },
  4: { drop: 660, peak: 18.5, end: 8, shape: 1.4 },
  5: { drop: 690, peak: 17.5, end: 8, shape: 1.2 },
};
const YELLOW_TEMP = 150;
const CRACK_TEMP = 196;

/** °C per minute from measured points (real data), only after the turning point, ~30 s window */
function rateOfRise(points, turning) {
  const after = points.filter(([t]) => t >= turning);
  return after.map(([t], i) => {
    const a = after[Math.max(0, i - 3)];
    const b = after[Math.min(after.length - 1, i + 3)];
    return [t, b[0] === a[0] ? 0 : ((b[1] - a[1]) / (b[0] - a[0])) * 60];
  });
}

/**
 * Illustrative roast in the shape roasting software draws (Cropster/Artisan): BT falls to the turning point,
 * RoR climbs from zero to its peak about 1:40 later, then declines steadily and drops faster after first crack.
 */
function mockRoast(level) {
  const p = ROAST_PRESETS[level] || ROAST_PRESETS[1];
  const charge = 205;
  const turning = 75;
  const turningTemp = 94;
  const delay = 100;
  const peakAt = turning + delay;
  const bean = [];
  const ror = [];
  for (let t = 0; t < turning; t += 3) {
    bean.push([t, turningTemp + (charge - turningTemp) * Math.pow((turning - t) / turning, 2.4)]);
  }
  // raw rate (°C/min) for each second after the turning point; the post-crack dip needs the crack time
  const rawRates = (crackAt) => {
    const out = [];
    for (let t = turning; t <= p.drop; t += 1) {
      let rate;
      if (t <= peakAt) {
        rate = p.peak * (1 - Math.pow(1 - (t - turning) / delay, 2.2));
      } else {
        rate = p.end + (p.peak - p.end) * Math.pow(1 - (t - peakAt) / (p.drop - peakAt), p.shape);
        if (crackAt != null && t > crackAt) {
          const w = (t - crackAt) / Math.max(1, p.drop - crackAt);
          rate *= 1 - 0.35 * w * w * (3 - 2 * w);
        }
      }
      out.push(rate);
    }
    return out;
  };
  // smooth the peak (no kink), like the filtered RoR roasting software shows
  const smooth = (values, half = 20) =>
    values.map((_, i) => {
      // symmetric window that narrows near the ends, so the curve starts at 0 at the turning point
      const h = Math.min(half, i, values.length - 1 - i);
      let sum = 0;
      for (let k = i - h; k <= i + h; k++) sum += values[k];
      return sum / (2 * h + 1);
    });
  const integrate = (rates) => {
    let temp = turningTemp;
    let dryEnd = null;
    let crack = null;
    const points = [];
    rates.forEach((rate, i) => {
      const t = turning + i;
      if (dryEnd == null && temp >= YELLOW_TEMP) dryEnd = t;
      if (crack == null && temp >= CRACK_TEMP) crack = t;
      if (i % 3 === 0 || i === rates.length - 1) points.push([t, temp, rate]);
      temp += rate / 60;
    });
    return { points, dryEnd, crack };
  };
  const firstPass = integrate(rawRates(null));
  const { points, dryEnd, crack } = integrate(smooth(rawRates(firstPass.crack)));
  points.forEach(([t, temp, rate]) => {
    bean.push([t, temp]);
    ror.push([t, rate]);
  });
  return { bean, air: [], ror, turning, dryEnd, crack, drop: p.drop };
}

function normalizeRoast(json) {
  const bean = (json.bean || []).map(([t, value]) => [Number(t), Number(value)]).filter(([t, v]) => isFinite(t) && isFinite(v));
  if (bean.length < 4) return null;
  const air = (json.air || []).map(([t, value]) => [Number(t), Number(value)]);
  const minimum = bean.reduce((low, point) => (point[1] < low[1] ? point : low), bean[0]);
  const drop = Number(json.drop) || bean[bean.length - 1][0];
  const turning = Number(json.turning) || minimum[0];
  return {
    bean,
    air,
    turning,
    dryEnd: Number(json.dry_end) || bean.find(([t, v]) => t > turning && v >= 150)?.[0],
    crack: Number(json.first_crack) || null,
    drop,
    ror: Array.isArray(json.ror) ? json.ror.map(([t, value]) => [Number(t), Number(value)]) : null,
    machine: json.machine,
    date: json.date,
    batch: json.batch,
  };
}

function valueAt(points, t) {
  if (!points.length) return null;
  if (t <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [t1, v1] = points[i];
    if (t <= t1) {
      const [t0, v0] = points[i - 1];
      return v0 + ((v1 - v0) * (t - t0)) / (t1 - t0 || 1);
    }
  }
  return points[points.length - 1][1];
}

class DropitRoastChart extends HTMLElement {
  connectedCallback() {
    onNear(this, () => this.draw());
  }

  data() {
    const json = this.querySelector('script[type="application/json"]');
    if (json) {
      try {
        const data = normalizeRoast(JSON.parse(json.textContent));
        if (data) return data;
      } catch {
        /* fall back to the illustrative curve */
      }
    }
    return mockRoast(Number(this.dataset.level) || 1);
  }

  draw() {
    const data = this.data();
    data.ror ||= rateOfRise(data.bean, data.turning);
    const host = this.querySelector('[data-svg]');
    if (!host) return;

    // One panel like roasting software: BT on the left axis, RoR on the right axis, events as callouts
    const mobile = matchMedia('(max-width: 749px)').matches;
    const W = Math.max(320, Math.round(host.clientWidth || 800));
    const H = mobile ? 300 : 400;
    const pad = { l: 44, r: 40, t: 16, b: 28 };
    const plotH = H - pad.t - pad.b;
    const temps = data.bean.map(([, v]) => v);
    const T0 = Math.floor((Math.min(...temps) - 6) / 20) * 20;
    const T1 = Math.ceil((Math.max(...temps) + 4) / 20) * 20;
    const steps = (T1 - T0) / 20;
    const rates = data.ror.map(([, v]) => v);
    const R1 = steps * Math.ceil((Math.max(...rates) + 1) / steps);
    const tMax = data.drop;
    const x = (t) => pad.l + (t / tMax) * (W - pad.l - pad.r);
    const y = (v) => pad.t + ((T1 - v) / (T1 - T0)) * plotH;
    const yr = (r) => pad.t + ((R1 - Math.min(R1, Math.max(0, r))) / R1) * plotH;
    const path = (points, fy) => points.map(([t, v], i) => `${i ? 'L' : 'M'}${x(t).toFixed(1)},${fy(v).toFixed(1)}`).join('');
    const parts = [];

    // grid: 5 temperature steps on the left, matching rate steps on the right
    for (let i = 0; i <= steps; i++) {
      const v = T0 + ((T1 - T0) * i) / steps;
      const r = (R1 * i) / steps;
      parts.push(`<line class="dp-roast__grid" x1="${pad.l}" x2="${W - pad.r}" y1="${y(v)}" y2="${y(v)}"/>`);
      parts.push(`<text x="${pad.l - 8}" y="${y(v) + 4}" text-anchor="end">${Math.round(v)}°</text>`);
      parts.push(`<text class="dp-roast__axis-ror" x="${W - pad.r + 8}" y="${yr(r) + 4}">${Math.round(r)}</text>`);
    }
    const tick = mobile ? 120 : 60;
    for (let t = 0; t <= tMax; t += tick) {
      parts.push(`<text x="${x(t)}" y="${H - 6}" text-anchor="middle">${clock(t)}</text>`);
    }

    // curves, revealed left to right
    const id = `roast-clip-${Math.random().toString(36).slice(2, 8)}`;
    parts.push(
      `<clipPath id="${id}"><rect class="dp-roast__reveal" x="0" y="0" width="${W}" height="${H}"/></clipPath>`,
      `<g clip-path="url(#${id})">`,
      data.air.length ? `<path class="dp-roast__line dp-roast__line--air" d="${path(data.air, y)}"/>` : '',
      `<path class="dp-roast__line dp-roast__line--ror" d="${path(data.ror, yr)}"/>`,
      `<path class="dp-roast__line dp-roast__line--bean" d="${path(data.bean, y)}"/>`,
      '</g>'
    );

    // events: dot on the bean curve + callout box
    const events = [
      [data.turning, 'Ponto de virada', 'right-up'],
      [data.dryEnd, 'Amarelo', 'left-up'],
      [data.crack, '1º crack', 'right-down'],
    ].filter(([t]) => t != null);
    const boxW = 124;
    const boxH = 38;
    for (const [t, label, side] of events) {
      const v = valueAt(data.bean, t);
      const cx = x(t);
      const cy = y(v);
      let bx = side.startsWith('right') ? cx + 10 : cx - boxW - 10;
      let by = side.endsWith('up') ? cy - boxH - 10 : cy + 10;
      if (bx + boxW > W - pad.r) bx = cx - boxW - 10;
      if (bx < pad.l) bx = cx + 10;
      by = Math.min(Math.max(by, pad.t), pad.t + plotH - boxH);
      parts.push(`<circle class="dp-roast__dot" cx="${cx}" cy="${cy}" r="4.5"/>`);
      parts.push(
        `<g class="dp-roast__callout"><rect x="${bx}" y="${by}" width="${boxW}" height="${boxH}" rx="6"/>` +
          `<text x="${bx + 10}" y="${by + 16}">${label}</text>` +
          `<text class="dp-roast__callout-value" x="${bx + 10}" y="${by + 30}">${clock(t)} · ${Math.round(v)} °C</text></g>`
      );
    }

    parts.push(
      `<line class="dp-roast__cursor" data-cursor x1="0" x2="0" y1="${pad.t}" y2="${pad.t + plotH}" visibility="hidden"/>`,
      `<rect data-hit x="${pad.l}" y="${pad.t}" width="${W - pad.l - pad.r}" height="${plotH}" fill="transparent"/>`
    );

    host.style.aspectRatio = `${W} / ${H}`;
    host.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" role="img" aria-label="Curva de torra: temperatura do grão e taxa de aumento ao longo de ${clock(
      data.drop
    )} minutos">${parts.join('')}</svg>`;

    const reveal = host.querySelector('.dp-roast__reveal');
    if (reveal && !reducedMotion) {
      reveal.setAttribute('width', '0');
      const start = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - start) / 1600);
        reveal.setAttribute('width', String(W * (1 - Math.pow(1 - k, 3))));
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    // readout on hover / touch
    const svg = host.querySelector('svg');
    const cursor = host.querySelector('[data-cursor]');
    const tip = this.querySelector('[data-tip]');
    const move = (event) => {
      const box = svg.getBoundingClientRect();
      const px = ((event.clientX - box.left) / box.width) * W;
      const t = Math.min(tMax, Math.max(0, ((px - pad.l) / (W - pad.l - pad.r)) * tMax));
      const bean = valueAt(data.bean, t);
      const air = data.air.length ? valueAt(data.air, t) : null;
      const ror = t >= (data.ror[0]?.[0] ?? Infinity) ? valueAt(data.ror, t) : null;
      cursor.setAttribute('x1', x(t));
      cursor.setAttribute('x2', x(t));
      cursor.setAttribute('visibility', 'visible');
      if (!tip) return;
      tip.hidden = false;
      tip.style.left = `${Math.min(88, Math.max(12, (x(t) / W) * 100))}%`;
      tip.innerHTML = `<strong>${clock(t)}</strong><span>Grão ${Math.round(bean)} °C</span>${
        air != null ? `<span>Ar ${Math.round(air)} °C</span>` : ''
      }${ror != null ? `<span>Taxa ${ror.toFixed(1).replace('.', ',')} °C/min</span>` : ''}`;
    };
    const hit = host.querySelector('[data-hit]');
    hit?.addEventListener('pointermove', move);
    hit?.addEventListener('pointerdown', move);
    hit?.addEventListener('pointerleave', () => {
      cursor.setAttribute('visibility', 'hidden');
      if (tip) tip.hidden = true;
    });

    // phases: 100% stacked bar (drying / Maillard / development)
    const bar = this.querySelector('[data-phases]');
    if (bar && data.dryEnd && data.crack) {
      const seg = (name, a, b) => {
        const pct = ((b - a) / data.drop) * 100;
        return `<div class="dp-roast__seg" style="flex: ${b - a}" title="${name}"><span>${clock(b - a)} · ${pct
          .toFixed(1)
          .replace('.', ',')}%</span></div>`;
      };
      bar.innerHTML =
        `<div class="dp-roast__bar">${seg('Secagem', 0, data.dryEnd)}${seg('Maillard', data.dryEnd, data.crack)}${seg(
          'Desenvolvimento',
          data.crack,
          data.drop
        )}</div>` +
        `<div class="dp-roast__bar-legend"><span>Secagem</span><span>Maillard</span><span>Desenvolvimento</span><span class="dp-roast__bar-total">Torra total ${clock(
          data.drop
        )} · ${Math.round(valueAt(data.bean, data.drop))} °C</span></div>`;
    }
  }
}

if (!customElements.get('dropit-roast-chart')) customElements.define('dropit-roast-chart', DropitRoastChart);

/* ---------- Brew guide ---------- */
class DropitBrew extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = (i + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });
    this.panels.forEach((panel) => panel && this.setupTimer(panel));
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = i !== index;
      if (i !== index) this.panels[i]?.dispatchEvent(new Event('dp:stop'));
    });
  }

  setupTimer(panel) {
    const steps = panel.querySelector('[data-steps]');
    const display = panel.querySelector('[data-clock]');
    const start = panel.querySelector('[data-start]');
    const label = panel.querySelector('[data-start-label]');
    const reset = panel.querySelector('[data-reset]');
    const items = [...panel.querySelectorAll('[data-at]')];
    if (!steps || !start || !items.length) {
      panel.querySelector('.dp-brew__timer')?.remove();
      return;
    }
    let elapsed = 0;
    let startedAt = 0;
    let interval = 0;

    const paint = (seconds) => {
      display.textContent = clock(seconds);
      let current = -1;
      items.forEach((item, i) => {
        if (Number(item.dataset.at) <= seconds) current = i;
      });
      items.forEach((item, i) => {
        item.classList.toggle('is-now', i === current);
        item.classList.toggle('is-done', i < current);
      });
    };
    const stop = () => {
      if (!interval) return;
      clearInterval(interval);
      interval = 0;
      elapsed += (performance.now() - startedAt) / 1000;
      label.textContent = 'Continuar';
    };
    start.addEventListener('click', () => {
      if (interval) return stop();
      startedAt = performance.now();
      steps.setAttribute('data-running', '');
      reset.hidden = false;
      label.textContent = 'Pausar';
      interval = setInterval(() => paint(elapsed + (performance.now() - startedAt) / 1000), 250);
      paint(elapsed);
    });
    reset.addEventListener('click', () => {
      stop();
      elapsed = 0;
      paint(0);
      steps.removeAttribute('data-running');
      reset.hidden = true;
      label.textContent = 'Iniciar timer';
    });
    panel.addEventListener('dp:stop', stop);
  }
}

if (!customElements.get('dropit-brew')) customElements.define('dropit-brew', DropitBrew);

/* ---------- Vertical video ---------- */
class DropitVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    const sound = this.querySelector('[data-sound]');
    if (!this.video) return;
    this.video.muted = true;
    this.video.playsInline = true;
    sound?.addEventListener('click', () => {
      this.video.muted = !this.video.muted;
      sound.setAttribute('aria-pressed', String(!this.video.muted));
      sound.setAttribute('aria-label', this.video.muted ? 'Ativar som' : 'Desativar som');
      if (this.video.paused) this.video.play().catch(() => {});
    });
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          if (this.video.preload === 'none') this.video.preload = 'auto';
          if (!reducedMotion) this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      },
      { threshold: 0.35 }
    ).observe(this);
  }
}

if (!customElements.get('dropit-video')) customElements.define('dropit-video', DropitVideo);

/* ---------- Photo gallery below the text (accessories): tap opens a full-screen viewer ---------- */
class DropitPhotos extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-photos-viewer]');
    this.track = this.dialog?.querySelector('[data-photos-track]');
    if (!this.dialog || !this.track) return;
    this.slides = [...this.track.children];
    this.count = this.dialog.querySelector('[data-photos-count]');
    this.prev = this.dialog.querySelector('[data-photos-prev]');
    this.next = this.dialog.querySelector('[data-photos-next]');
    this.index = 0;

    this.querySelectorAll('[data-photo-open]').forEach((button) => {
      button.addEventListener('click', () => this.open(Number(button.getAttribute('data-photo-open'))));
    });
    this.dialog.querySelector('[data-photos-close]')?.addEventListener('click', () => this.dialog.close());
    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    // A tap on the dark area around the photo closes
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.classList?.contains('dp-photos__slide')) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
  }

  open(index) {
    this.slides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
    this.dialog.showModal();
    this.track.scrollLeft = index * this.track.clientWidth;
    this.update();
  }

  go(index) {
    const target = Math.min(this.slides.length - 1, Math.max(0, index));
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  update() {
    const width = this.track.clientWidth || 1;
    this.index = Math.round(this.track.scrollLeft / width);
    if (this.count) this.count.textContent = String(this.index + 1).padStart(2, '0');
    if (this.prev) this.prev.disabled = this.index <= 0;
    if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
  }
}

if (!customElements.get('dropit-photos')) customElements.define('dropit-photos', DropitPhotos);

/* ---------- Grind: "Quero moído" switch + methods dialog ---------- */
/**
 * Drives Horizon's hidden "Moagem" option. Off = the Grãos value. Turning it on opens a dialog with the methods;
 * picking one selects the value whose name contains the method's word (fina / grossa). Closing without picking
 * keeps grãos. The method travels with the order as the line item property "Preparo".
 */
class DropitGrind extends HTMLElement {
  connectedCallback() {
    this.info = this.closest('.dp-info');
    if (!this.info) return;
    this.storageKey = `dropit-grind:${this.dataset.product}`;
    this.switch = this.querySelector('[data-switch]');
    this.dialog = this.querySelector('[data-dialog]');
    this.methods = [...this.querySelectorAll('[data-method]')];
    this.summary = this.querySelector('[data-summary]');
    this.summaryText = this.querySelector('[data-summary-text]');
    this.note = this.querySelector('[data-note]');
    try {
      this.method = sessionStorage.getItem(this.storageKey) || '';
    } catch {
      this.method = '';
    }
    if (!this.inputs().length) return;

    // The dialog lives in <body> so the morphing variant picker area can't touch it
    document.body.append(this.dialog);

    this.switch.addEventListener('click', () => {
      if (this.switch.getAttribute('aria-checked') === 'true') this.pick('beans');
      else this.open();
    });
    this.querySelector('[data-change]')?.addEventListener('click', () => this.open());
    this.methods.forEach((button) =>
      button.addEventListener('click', () => {
        this.chooseMethod(button.dataset.method);
        this.dialog.close();
      })
    );
    this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-beans]')?.addEventListener('click', () => {
      this.pick('beans');
      this.dialog.close();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    const section = this.closest('.shopify-section') || document;
    section.addEventListener('shopify:product:select', (event) => {
      event.promise?.then(() => setTimeout(() => this.sync(), 0)).catch(() => {});
    });

    this.setAttribute('data-ready', '');
    this.sync();
  }

  open() {
    if (this.switch.disabled) return;
    this.methods.forEach((button) =>
      button.setAttribute('aria-pressed', String(this.kind() !== 'beans' && button.dataset.method === this.method))
    );
    this.dialog.showModal();
  }

  inputs() {
    return [...this.info.querySelectorAll('variant-picker input[data-option-name]')].filter(
      (input) => input.dataset.optionName.toLowerCase() === 'moagem'
    );
  }

  isBeans(value) {
    const v = value.toLowerCase();
    return v.startsWith('grão') || v.startsWith('grao');
  }

  inputFor(kind) {
    return this.inputs().find((input) =>
      kind === 'beans' ? this.isBeans(input.value) : input.value.toLowerCase().includes(kind)
    );
  }

  kind() {
    const checked = this.inputs().find((input) => input.checked);
    if (!checked || this.isBeans(checked.value)) return 'beans';
    return checked.value.toLowerCase().includes('grossa') ? 'grossa' : 'fina';
  }

  methodButton(name = this.method) {
    return this.methods.find((button) => button.dataset.method === name);
  }

  remember(name) {
    this.method = name || '';
    try {
      if (name) sessionStorage.setItem(this.storageKey, name);
    } catch {
      /* private mode */
    }
  }

  chooseMethod(name) {
    const button = this.methodButton(name);
    if (!button) return;
    this.remember(name);
    this.pick(button.dataset.grind);
  }

  pick(kind) {
    const input = this.inputFor(kind);
    if (!input) return;
    if (!input.checked) input.click();
    this.sync();
  }

  sync() {
    const checked = this.inputs().find((input) => input.checked);
    const beansInput = this.inputFor('beans');
    const groundAvailable = this.inputs().some(
      (input) => input !== beansInput && input.dataset.optionAvailable !== 'false'
    );
    const kind = this.kind();
    const ground = kind !== 'beans';

    // Ground picked for a size that only comes in beans (e.g. 18 g): fall back to beans
    if (ground && checked?.dataset.optionAvailable === 'false' && beansInput) {
      beansInput.click();
      return;
    }
    if (ground && !this.methodButton()?.dataset.grind.includes(kind)) {
      this.remember(this.methods.find((button) => kind.includes(button.dataset.grind))?.dataset.method);
    }

    this.switch.setAttribute('aria-checked', String(ground));
    this.switch.disabled = !groundAvailable;
    this.summary.hidden = !ground;
    if (ground && checked) this.summaryText.textContent = `${this.method || 'Moído'} · ${checked.value.toLowerCase()}`;
    // Size that only comes in beans (e.g. 18 g): hide the switch instead of showing a disabled one
    this.hidden = !groundAvailable;

    const form = this.info.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      let property = form.querySelector('input[name="properties[Preparo]"]');
      if (ground && this.method) {
        if (!property) {
          property = document.createElement('input');
          property.type = 'hidden';
          property.name = 'properties[Preparo]';
          form.append(property);
        }
        property.value = this.method;
      } else {
        property?.remove();
      }
    }
  }
}

if (!customElements.get('dropit-grind')) customElements.define('dropit-grind', DropitGrind);

/* ---------- Recommendations ---------- */
function initRecommendations() {
  for (const shelf of document.querySelectorAll('[data-dp-recs]')) {
    if (shelf.querySelector('.dropit-shelf__track') || !shelf.dataset.url) continue;
    onNear(
      shelf,
      () => {
        fetch(shelf.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-dp-recs]');
            if (fresh?.querySelector('.dropit-shelf__track')) shelf.innerHTML = fresh.innerHTML;
          })
          .catch(() => {});
      },
      '800px 0px'
    );
  }
}


/* ---------- Full-screen vertical video (play after the last photo) ---------- */
function initVideoModal() {
  const modal = document.querySelector('[data-dp-video-modal]');
  if (!(modal instanceof HTMLDialogElement)) return;
  const video = modal.querySelector('video');
  const close = () => {
    video?.pause();
    modal.close();
  };
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-dp-video-open]')) {
      modal.showModal();
      video?.play().catch(() => {});
    } else if (target?.closest('[data-dp-video-close]') || target === modal) {
      close();
    }
  });
  modal.addEventListener('close', () => video?.pause());
}

initBuy();
initVideoModal();
initNav();
initClamp();
initRecommendations();

/**
 * Stock line above the price (snippets/dropit-stock.liquid): re-read the selected variant in the JSON map.
 * @param {Element} root
 * @param {any} variant
 */
function updateStock(root, variant) {
  const line = root.querySelector('[data-dp-stock]');
  const mapEl = root.querySelector('[data-dp-stock-map]');
  if (!line || !mapEl || !variant) return;
  let map = {};
  try { map = JSON.parse(mapEl.textContent || '{}'); } catch (error) { return; }
  const id = String(variant.id ?? '').split('/').pop();
  const v = map[id];
  if (!v) return;
  let level = 'ok';
  let text = 'Produto em estoque';
  if (!v.available) { level = 'out'; text = 'Esgotado'; }
  else if (v.tracked && v.qty <= 1) { level = 'last'; text = 'Última unidade'; }
  else if (v.tracked && v.qty <= 5) { level = 'low'; text = `Últimas ${v.qty} unidades`; }
  line.dataset.level = level;
  const label = line.querySelector('[data-dp-stock-text]');
  if (label) label.textContent = text;
}
